import { useMemo } from 'react'
import { QueryHookOptions } from '@apollo/client'
import moment from 'moment'
import {
  usePagination,
  UsePaginationOptions,
  UsePaginationResult,
} from './pagination'
import {
  CustomerDocument,
  CustomerQuery,
  CustomerQueryHookResult,
  CustomerQueryResult,
  CustomerQueryVariables,
  CustomersQuery,
  CustomersQueryHookResult,
  CustomersQueryVariables,
  CustomerType,
  CustomerUpdateMutationResult,
  CustomerUpdateMutationVariables,
  UnregisteredCustomersQuery,
  UnregisteredCustomersQueryHookResult,
  UnregisteredCustomersQueryVariables,
  useCustomerQuery,
  useCustomersQuery,
  useCustomerUpdateMutation,
  useUnregisteredCustomersQuery,
} from 'lib/graphql/storeadmin'
import { Customer, CustomerBooking } from 'components/customers'

/**
 * インデックス番号を算出する処理
 * @param page
 * @param size
 * @param index
 */
function calcIndex(page: number, size: number, index: number): number {
  return (page - 1) * size + index + 1
}

type CustomerData = CustomersQueryHookResult['data']
type UnregisteredCustomerData = UnregisteredCustomersQueryHookResult['data']

/**
 * GraphQL で取得したデータを DataSource に整形する処理
 * @param data データ
 * @param page 現在のページ
 * @param size 1ページごとに表示される件数
 */
function getCustomersDataSource(
  data: CustomerData,
  page: number,
  size: number
): Customer[] {
  return (
    data?.customers?.edges.map(({ customer }, index) => {
      const {
        id,
        address,
        email,
        phone,
        corporationName,
        firstName,
        lastName,
        type,
        bookableFlag,
        createdAt,
      } = customer
      let name
      let personInCharge

      if (type === CustomerType.Personal) {
        name = `${lastName} ${firstName}`
      } else if (type === CustomerType.Corporate) {
        name = corporationName
        personInCharge = `${lastName} ${firstName}`
      }

      return {
        key: id,
        no: calcIndex(page, size, index),
        name: name?.includes('default_name') || name === null ? '未登録' : name, // 会社名が未登録の場合===null
        personInCharge,
        email,
        bookableFlag,
        createdAt,
        type,
        phone: phone ? phone : '未登録',
        address: address ? `${address.prefecture} ${address.city}` : '未登録',
        numberOfReservations: 0,
      }
    }) || []
  )
}

function getUnregisteredCustomersDataSource(
  data: UnregisteredCustomerData,
  page: number,
  size: number
): Customer[] {
  return (
    data?.customers?.edges.map(({ customer }, index) => {
      const { id, email, createdAt } = customer

      return {
        key: id,
        no: calcIndex(page, size, index),
        email,
        createdAt,
      }
    }) || []
  )
}

type Orders =
  | CustomerQueryHookResult['data']['futureOrders']
  // eslint-disable-next-line
  // @ts-ignore
  | CustomerQueryHookResult['data']['pastOrders']

/**
 * GraphQL で取得した顧客に紐づく予約データを DataSource に整形する処理
 * @param orders
 */
export function getOrdersDataSource(orders?: Orders): CustomerBooking[] {
  return (
    orders?.edges?.map(({ order }) => {
      const contents = Array.from(
        new Set(
          order.lineItems?.edges?.map(({ lineItem }) => lineItem?.name?.jaJp)
        )
      )
      return {
        key: order.id,
        orderNumber: order.orderNumber,
        bookingDates: order.bookingDates,
        contents: contents,
        originalTotalPrice: order.totalPrice,
        checkinStatus: order.bookingDates,
        paymentStatus: order.payment,
        status: order.status,
        approval: order.approval,
      }
    }) || []
  )
}

type PaginationResult = UsePaginationResult<
  CustomersQueryHookResult | UnregisteredCustomersQueryHookResult
>
type PaginationOptions = UsePaginationOptions<
  CustomersQuery | UnregisteredCustomersQuery,
  CustomersQueryVariables | UnregisteredCustomersQueryVariables,
  CustomersQueryHookResult | UnregisteredCustomersQueryHookResult
>

export type UseCustomersOptions = Omit<PaginationOptions, 'queryHooks'> & {
  type?: CustomerType
}

export interface UseCustomersResult extends Omit<PaginationResult, 'data'> {
  data: Customer[]
}
export interface UseUnregisteredCustomersResult
  extends Omit<PaginationResult, 'data'> {
  data: Customer[]
}

/**
 * 顧客データ一覧を取得するための Custom Hooks
 * @param options
 */
export function useCustomers(
  options?: UseCustomersOptions
): UseCustomersResult {
  const type = options?.type
  const customersQuery =
    type === CustomerType.Unregistered
      ? useUnregisteredCustomersQuery
      : useCustomersQuery
  const getCustomerDataSource =
    type === CustomerType.Unregistered
      ? getUnregisteredCustomersDataSource
      : getCustomersDataSource

  const { data, page, size, ...rest } = usePagination<
    CustomersQuery | UnregisteredCustomersQuery,
    CustomersQueryVariables | UnregisteredCustomersQueryVariables,
    CustomersQueryHookResult | UnregisteredCustomersQueryHookResult
  >({
    ...options,
    queryHooks: customersQuery,
  })

  const dataSource = getCustomerDataSource(data, page, size)

  return {
    data: dataSource,
    page,
    size,
    ...rest,
  }
}

type UseCustomerQueryOptions = QueryHookOptions<
  CustomerQuery,
  CustomerQueryVariables
>
export interface UseCustomerOptions
  extends Omit<UseCustomerQueryOptions, 'variables'> {}

export interface UseCustomerResult extends Omit<CustomerQueryResult, 'data'> {
  query: typeof CustomerDocument
  customer: CustomerQueryResult['data']['customer']
  futureOrders: CustomerBooking[]
  allOrders: CustomerBooking[]
  update: (
    variables: Omit<CustomerUpdateMutationVariables, 'id'>
  ) => Promise<CustomerUpdateMutationResult>
}

/**
 * ID に一致する顧客データを取得するための Custom Hooks
 * @param id Customer ID
 * @param options Apollo query hooks options
 */
export function useCustomer(
  id: string,
  options?: UseCustomerOptions
): UseCustomerResult {
  const { data, variables, ...rest } = useCustomerQuery({
    variables: { id: id, future: moment().format('YYYY-MM-DD') },
    ...options,
  })

  const [updateCustomer] = useCustomerUpdateMutation({
    refetchQueries: [{ query: CustomerDocument, variables }],
    awaitRefetchQueries: true,
  })

  const { customer, futureOrders, allOrders } = data || {}
  const futureOrdersDataSource = useMemo<CustomerBooking[]>(
    () => getOrdersDataSource(futureOrders),
    [futureOrders]
  )
  const allOrdersDataSource = useMemo<CustomerBooking[]>(
    () => getOrdersDataSource(allOrders),
    [allOrders]
  )

  const update = async (
    variables: Omit<CustomerUpdateMutationVariables, 'id'>
  ): // eslint-disable-next-line
  // @ts-ignore
  CustomerUpdateMutationResult => {
    // eslint-disable-next-line
    // @ts-ignore
    return await updateCustomer({ variables: { id, ...variables } })
  }

  return {
    query: CustomerDocument,
    customer,
    futureOrders: futureOrdersDataSource,
    allOrders: allOrdersDataSource,
    variables,
    ...rest,
    // eslint-disable-next-line
    // @ts-ignore
    update,
  }
}

// Common error handle function for Customers
export function customerErrorHandling(userErrors): string[] {
  const errorMessages = []
  userErrors.forEach((value) => {
    switch (value.code) {
      case 'PASSWORD_TOO_WEAK':
        errorMessages.push(
          `パスワードには次の要件が必要です。\n・ 長さ8文字以上\n・次のうち少なくとも3種類 : \n    - 小文字(a-z)\n    - 大文字(A-Z)\n    - 数字(0-9)\n    - 特別文字(例 !@#$%^&*)\n・同じ文字の連続は2回まで`
        )
        break
      case 'AUTH0_USERS_EMAIL_IS_NULL':
        errorMessages.push(
          `パスワードを入力する場合はメールアドレスも入力してください。`
        )
        break
      case 'AUTH0_USER_ALREADY_EXITS':
        errorMessages.push(`このメールアドレスは既にAuth0に登録されています。`)
        break
      case 'BAD_DOMAIN':
        errorMessages.push('入力メールに無効なドメイン名が含まれています。')
        break
      case 'BLANK':
        errorMessages.push('入力値が空白です。')
        break
      case 'CONTAINS_HTML_TAGS':
        errorMessages.push('入力値にHTMLタグが含まれています。')
        break
      case 'CONTAINS_URL':
        errorMessages.push('入力値にURLが含まれています。')
        break
      case 'CUSTOMER_DISABLED':
        errorMessages.push('お客様は無効になっています。')
        break
      case 'INVALID':
        if (value.field.indexOf('email') !== -1) {
          errorMessages.push('メールアドレスの値が無効です。')
        } else {
          errorMessages.push('エラーが発生しました。')
        }
        break
      // TODO: API 側の修正後に再定義する
      case 'NOT_FOUND':
        if (value.field.indexOf('zip') !== -1) {
          errorMessages.push('郵便番号を確認してください。')
        } else {
          errorMessages.push('住所が存在しません。')
        }
        break
      case 'TAKEN':
        if (value.field.indexOf('email') !== -1) {
          errorMessages.push(
            '入力されたメールアドレスはすでに使用されています。'
          )
        } else {
          errorMessages.push('入力された値はすでに使用されています。')
        }
        break
      case 'TOKEN_INVALID':
        errorMessages.push('無効なアクティベーショントークン。')
        break
      case 'TOO_LONG':
        if (
          value.field.indexOf('firstName') !== -1 ||
          value.field.indexOf('lastName') !== -1
        ) {
          errorMessages.push('名前を確認してください。')
        } else if (value.field.indexOf('phone') !== -1) {
          errorMessages.push('電話番号が有効であることを確認してください。')
        } else if (value.field.indexOf('zip') !== -1) {
          errorMessages.push('郵便番号が有効であることを確認してください。')
        } else {
          errorMessages.push('入力値が長すぎないことを確認してください。')
        }
        break
      case 'TOO_SHORT':
        if (value.field[0] === 'firstName' || value.field[0] === 'lastName') {
          errorMessages.push('名前を確認してください。')
        } else if (value.field.indexOf('phone') !== -1) {
          errorMessages.push('電話番号が有効であることを確認してください。')
        } else if (value.field.indexOf('zip') !== -1) {
          errorMessages.push('郵便番号が有効であることを確認してください。')
        } else {
          errorMessages.push('入力値が短すぎないことを確認してください。')
        }
        break

      case 'UNDEFINED_CUSTOMER':
        errorMessages.push('登録されていない顧客です。')
        break
      case 'AUTH0_INVALID_URL':
        errorMessages.push(
          'オブジェクトがフォーマットuser-idの検証に合格しませんでした。'
        )
        break
      case 'AUTH0_EMAIL_NOT_FOUND':
        errorMessages.push(
          'Auth0ユーザーに一致するメールアドレスがありませんでした。'
        )
        break
      case undefined:
        errorMessages.push(...errorMessages)
        break
      default:
        errorMessages.push('エラーが発生しました。')
        break
    }
  })
  // Filter Duplicate item and return unique Array
  return errorMessages?.filter((e, i) => errorMessages?.indexOf(e) === i)
}
